import React, { useEffect, useMemo, useState, useContext } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import visaLogo from "../assets/images/visa.png";
import mastercardLogo from "../assets/images/mastercard.webp";
import stripeLogo from "../assets/images/stripe.webp";
import { UserContext } from "./UserContext";
import api from "../api/apiClient";
import DigitalImg from "../assets/audiobook.jpg";
import PrintImg from "../assets/book.jpg";
import BundleImg from "../assets/both.jpg";
import { trackEvent } from "../utils/pixel";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const USD_TO_SEK = 9.6; // Antagen kurs — justera vid behov

const tiers = [
  {
    id: 1,
    name: "Digitala Paketet",
    credits: 500,
    price: 29.95,
    priceSek: Math.round(29.95 * USD_TO_SEK),
    description: "Räcker till ca 35 min ljudbok + PDF. Perfekt som godnattsaga.",
    highlight: false,
  },
  {
    id: 2,
    name: "Populäraste",
    credits: 1200,
    price: 59.95,
    priceSek: Math.round(59.95 * USD_TO_SEK),
    description:
      "Räcker till digitala paketet + Fysisk bok i premiumkvalitet. 30 sidor i hårdpärm med fantastiska illustrationer. + 200 extra ⭐",
    highlight: true,
  },
  {
    id: 3,
    name: "Stora Paketet",
    credits: 1600,
    price: 79.95,
    priceSek: Math.round(79.95 * USD_TO_SEK),
    description:
      "Räcker till både Populäraste + Digitala paketet + 150 extra ⭐",
    highlight: false,
  },
];

const FormatCard = ({ label, image, selected, usdPrice, priceSek, starCount, description, onClick }) => (
  <div
    onClick={onClick}
    className={`p-4 rounded-lg transition-transform duration-200 bg-white h-full flex flex-col cursor-pointer ${
      selected ? "scale-105 border-4 border-blue-500 shadow-lg" : "border border-gray-200 shadow-sm hover:shadow-md"
    }`}
  >
    {image && <img src={image} alt={label} className="w-full h-full object-cover rounded mb-3" />}
    <h3 className="text-lg font-semibold text-center mb-2">{label}</h3>

    <div className="text-center mb-3">
      {starCount != null && (
        <p className="flex items-center justify-center text-2xl font-bold mb-1">
          {starCount}
          <FontAwesomeIcon icon={faStar} className="text-softYellow ml-1 text-2xl" />
        </p>
      )}
      {usdPrice != null && (
        <p className="text-sm text-gray-600">
          <span className="text-2xl font-semibold">${usdPrice.toFixed(2)}</span>
          {priceSek != null && <span className="text-lg ml-2 text-gray-700">~{priceSek} kr</span>}
        </p>
      )}
    </div>

    {description && <p className="text-lg text-gray-700 text-center mt-auto">{description}</p>}
  </div>
);

function CheckoutForm({ clientSecret, tier, discount, promoCodeId, onPaymentSuccess, onPaymentError }) {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const waitForCreditsUpdate = async (userId) => {
    const maxAttempts = 12;
    const delay = 1000;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        await api.get(`/api/get-credits?user_id=${userId}`);
        return true;
      } catch {
        await new Promise((r) => setTimeout(r, delay));
      }
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      setMessage("Stripe är inte initialiserat.");
      return;
    }

    // Pixel-event - klick på Betala (alla betalmetoder)
    if (window.fbq) {
      try {
        trackEvent("InitiateCheckout", {
          value: (tier.discountedPrice ?? tier.price).toFixed(2),
          currency: "SEK",
        });
      } catch (err) {
        console.warn("Pixel error:", err);
      }
    }

    setLoading(true);
    setMessage("");

    try {
      // confirmPayment hanterar både kort och Klarna (redirect när nödvändigt)
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // Klarna kan kräva redirect; se till att return_url finns
          return_url: `${window.location.origin}/payment-success`,
        },
        redirect: "if_required",
      });

      if (error) {
        setMessage(error.message || "Betalningen misslyckades.");
        onPaymentError?.(error.message || "Payment error");
        setLoading(false);
        return;
      }

      // Om paymentIntent finns direkt (kortbetalning utan redirect)
      if (paymentIntent && paymentIntent.status === "succeeded") {
        setMessage("Betalning genomförd!");
        // Vänta lite så webhook/DB hinner uppdatera
        await new Promise((r) => setTimeout(r, 1500));
        const ok = await waitForCreditsUpdate(currentUser.id);

        if (window.fbq && ok) {
          try {
            trackEvent("Purchase", {
              value: (tier.discountedPrice ?? tier.price).toFixed(2),
              currency: "SEK",
            });
          } catch (err) {
            console.warn("Pixel Purchase error:", err);
          }
        }

        onPaymentSuccess?.(ok);
      } else {
        // Här kommer vi troligen när redirect behövs (t.ex. Klarna) — Stripe gör redirect automatiskt.
        // Visa info till användaren att hen omdirigeras/att slutföra betalningen.
        setMessage("Du omdirigeras nu för att slutföra betalningen. Följ instruktionerna.");
      }
    } catch (err) {
      console.error("CheckoutForm error:", err);
      setMessage("Ett fel inträffade. Försök igen.");
      onPaymentError?.(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-white p-4 rounded shadow-sm">
        <PaymentElement />
      </div>

      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full bg-pink-500 text-white py-3 rounded hover:opacity-90 transition"
      >
        {loading ? "Behandlar..." : `Betala ~${Math.round((tier.discountedPrice ?? tier.price) * USD_TO_SEK)} kr`}
      </button>

      {message && <p className="text-center text-turquoise mt-2">{message}</p>}
    </form>
  );
}

const CreditModal = ({ isOpen, onClose, onPaymentSuccess }) => {
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoMessage, setPromoMessage] = useState("");
  const [discount, setDiscount] = useState(0);
  const [promoCodeId, setPromoCodeId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [intentError, setIntentError] = useState(null);

  const { currentUser } = useContext(UserContext);

  const tiersWithDiscount = useMemo(() => {
    return tiers.map((t) => ({
      ...t,
      discountedPrice: +(t.price * (1 - discount / 100)).toFixed(2),
      discountedPriceInCentsUSD: Math.round(t.price * 100 * (1 - discount / 100)),
    }));
  }, [discount]);

  const selectedTier = tiersWithDiscount.find((t) => t.id === selectedTierId);

  // Skapa/uppdatera PaymentIntent när användaren väljer ett paket eller rabatt ändras
  useEffect(() => {
    let mounted = true;
    async function createIntent() {
      if (!selectedTier || !currentUser) {
        setClientSecret(null);
        return;
      }

      setCreatingIntent(true);
      setIntentError(null);
      setClientSecret(null);

      try {
        // Räkna om pris till SEK-öre (Stripe expects smallest currency unit)
        const amountSekCents = Math.round((selectedTier.discountedPrice ?? selectedTier.price) * USD_TO_SEK * 100);
        const originalSekCents = Math.round(selectedTier.price * USD_TO_SEK * 100);

        const body = {
          user_id: currentUser.id,
          price_in_cents: amountSekCents,
          original_price_in_cents: originalSekCents,
          discount,
          promo_code_id: promoCodeId,
          tier_id: selectedTier.id,
          currency: "sek", // be backend skapa PaymentIntent i SEK så Klarna kan visas
        };

        const { data } = await api.post(`${API_BASE_URL}/create-payment-intent`, body);
        if (!mounted) return;
        if (data?.clientSecret) {
          setClientSecret(data.clientSecret);
        } else {
          setIntentError("Kunde inte hämta clientSecret från servern.");
        }
      } catch (err) {
        console.error("Fel vid skapande av PaymentIntent:", err);
        setIntentError("Fel vid skapande av betalning. Försök igen.");
      } finally {
        if (mounted) setCreatingIntent(false);
      }
    }

    createIntent();
    return () => {
      mounted = false;
    };
  }, [selectedTierId, discount, promoCodeId, currentUser]);

  const handleApplyPromo = async () => {
    if (!currentUser?.id || !promoCode.trim()) {
      setPromoMessage("Användar-ID och rabattkod krävs.");
      return;
    }
    try {
      const { data } = await api.post("/affiliate/check-promo-code", {
        user_id: currentUser.id,
        promo_code: promoCode.trim(),
      });
      if (data.alreadyUsed) setPromoMessage("Rabattkoden är redan använd.");
      else {
        setDiscount(data.discount);
        setPromoMessage(`Rabatt: ${data.discount}%`);
        setPromoCodeId(data.promo_code_id);
      }
    } catch (err) {
      console.error("apply promo error:", err);
      setPromoMessage("Fel vid tillämpning av rabattkod.");
    }
  };

  // Reset när modalen stängs
  useEffect(() => {
    if (!isOpen) {
      setSelectedTierId(null);
      setPromoCode("");
      setPromoMessage("");
      setDiscount(0);
      setPromoCodeId(null);
      setClientSecret(null);
      setIntentError(null);
      setCreatingIntent(false);
    }
  }, [isOpen]);

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-24 z-50 ${isOpen ? "" : "hidden"}`}>
      <div className="bg-white w-[90vw] max-w-lg rounded-lg p-6 space-y-6 overflow-auto max-h-[90vh] relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-2xl">✕</button>

        <h2 className="text-2xl font-bold text-center text-turquoise flex items-center justify-center space-x-2">
          <FontAwesomeIcon icon={faStar} className="text-softYellow" />
          <span>Fyll på Stjärnor</span>
        </h2>
        <p className="text-gray-600 text-center">Stjärnor används för att skapa ljudböcker och illustrationer.</p>

        {/* Paketval */}
        <div className="grid grid-cols-1 gap-4">
          {tiersWithDiscount.map((tier) => (
            <FormatCard
              key={tier.id}
              label={tier.name}
              image={tier.id === 1 ? DigitalImg : tier.id === 2 ? PrintImg : BundleImg}
              starCount={tier.credits}
              usdPrice={tier.discountedPrice}
              priceSek={Math.round(tier.priceSek * (1 - discount / 100))}
              description={tier.description}
              selected={selectedTierId === tier.id}
              onClick={() => setSelectedTierId(tier.id)}
            />
          ))}
        </div>

        {/* Rabatt & Betalning */}
        {selectedTier && (
          <div className="pt-4 border-t space-y-6">
            <label className="block text-center text-lg text-gray-700">Rabattkod</label>
            <div className="flex gap-2">
              <input type="text" className="flex-1 border px-3 py-2 rounded" placeholder="Ange kod" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} />
              <button onClick={handleApplyPromo} className="px-4 bg-pink-500 text-white rounded hover:opacity-90">Tillämpa</button>
            </div>
            {promoMessage && <p className="text-center text-green-600">{promoMessage}</p>}

            <div className="mt-2">
              {creatingIntent && <p>Laddar betalningsmetoder…</p>}
              {intentError && <p className="text-red-600">{intentError}</p>}

              {clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <CheckoutForm
                    clientSecret={clientSecret}
                    tier={selectedTier}
                    discount={discount}
                    promoCodeId={promoCodeId}
                    onPaymentSuccess={onPaymentSuccess}
                    onPaymentError={(msg) => console.error(msg)}
                  />
                </Elements>
              ) : (
                !creatingIntent && <p className="text-sm text-gray-600">Välj paket eller försök igen för att ladda betalningsalternativ.</p>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-gray-500 text-center mt-6">Betalningar hanteras av Stripe. Vi lagrar inga kortuppgifter.</p>

        <div className="flex justify-center gap-4">
          <img src={stripeLogo} alt="Stripe" className="h-6" />
          <img src={mastercardLogo} alt="Mastercard" className="h-6" />
          <img src={visaLogo} alt="Visa" className="h-6" />
        </div>
      </div>
    </div>
  );
};

export default CreditModal;
